import {
  addDays,
  getStringWeekDaysStartingFromDate,
} from "../../../../../../utils/dateUtils";
import { ServiceBay, ServiceBaySchedule } from "../../../../../../models/serviceBay";

import SchedulingCard from "./SchedulingCard";
import useMediaQuery from "@mui/material/useMediaQuery";

interface IWeekProps {
  serviceBays: ServiceBay[];
  firstWeekDay: Date;
  currentDate: Date;
  consultantMonthSchedule: ServiceBaySchedule[];
  openScheduleModal: (schedule: ServiceBaySchedule) => void;
}

const Week = ({
  serviceBays,
  firstWeekDay,
  currentDate,
  consultantMonthSchedule,
  openScheduleModal,
}: IWeekProps) => {
  const isResponsiveView = useMediaQuery("(max-width: 1024px)");

  const getCard = (date: Date, hour: number) => {
    if (getServiceBaySchedule(date, hour)) {
      return (
        <SchedulingCard
          serviceBaySchedule={getServiceBaySchedule(date, hour)!}
          openScheduleModal={() =>
            openScheduleModal(getServiceBaySchedule(date, hour)!)
          }
          atPoint="start"
          showScheduleDate={serviceBays.length === 1}
        />
      );
    } else if (getFullDayServiceBaySchedule(date, hour)) {
      return (
        <SchedulingCard
          serviceBaySchedule={getFullDayServiceBaySchedule(date, hour)!}
          openScheduleModal={() =>
            openScheduleModal(getFullDayServiceBaySchedule(date, hour)!)
          }
          atPoint="allDay"
          showScheduleDate={serviceBays.length === 1}
        />
      );
    } else if (getRemainingDayServiceBaySchedule(date, hour)) {
      return (
        <SchedulingCard
          serviceBaySchedule={getRemainingDayServiceBaySchedule(date, hour)!}
          openScheduleModal={() =>
            openScheduleModal(getRemainingDayServiceBaySchedule(date, hour)!)
          }
          atPoint="end"
          showScheduleDate={serviceBays.length === 1}
        />
      );
    }
    return null;
  };

  const getServiceBaySchedule = (
    date: Date,
    hour: number
  ): ServiceBaySchedule | undefined => {
    return consultantMonthSchedule.find(
      (schedule) =>
        schedule.startDate?.getDate() === date.getDate() &&
        schedule.startDate?.getMonth() === date.getMonth() &&
        schedule.startDate?.getFullYear() === date.getFullYear() &&
        schedule.startHour?.getHours() === hour
    );
  };

  const getFullDayServiceBaySchedule = (
    date: Date,
    hour: number
  ): ServiceBaySchedule | undefined => {
    if (hour !== 0) return undefined;
    return consultantMonthSchedule.find((schedule) => {
      if (
        !schedule.startDate ||
        !schedule.endDate ||
        (schedule.startDate?.getMonth() !== date.getMonth() && schedule.endDate?.getMonth() !== date.getMonth()) ||
        (schedule.startDate?.getFullYear() !== date.getFullYear() && schedule.endDate?.getFullYear() !== date.getFullYear())
      )
        return false;

      // 00h00 do date
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      // 23h59 do date
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      // Verifica se o schedule começa antes das 00h do date e termina depois de 23h59 do date
      return schedule.startDate < startOfDay && schedule.endDate > endOfDay;
    });
  };

  const getRemainingDayServiceBaySchedule = (
    date: Date,
    hour: number
  ): ServiceBaySchedule | undefined => {
    if (hour !== 0) return undefined;
    return consultantMonthSchedule.find((schedule) => {
      if (
        !schedule.startDate ||
        !schedule.endDate ||
        (schedule.startDate?.getMonth() !== date.getMonth() && schedule.endDate?.getMonth() !== date.getMonth()) ||
        (schedule.startDate?.getFullYear() !== date.getFullYear() && schedule.endDate?.getFullYear() !== date.getFullYear())
      )
        return false;

      // 00h00 do date
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      // 23h59 do date
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      // Desconsidera se o endDate for menor que o startOfDay ou se o startDate for maior que o endOfDay
      if (schedule.endDate < startOfDay || schedule.startDate > endOfDay) {
        return false;
      }
      
      // Verifica se o schedule começa antes das 00h do date e termina antes de 23h59 do date
      return schedule.startDate < startOfDay && schedule.endDate < endOfDay;
    });
  };

  return (
    <div className={isResponsiveView ? "mobile-week-container" : ""}>

    <table className="width-100pct table-fixed">
      <thead>
        <tr>
          <th className="width-60 height-20"></th>
          {getStringWeekDaysStartingFromDate(firstWeekDay).map((day, index) => (
            <th
              className={`height-20 text-size-12 text-medium ${
                currentDate.getDate() === firstWeekDay.getDate() + index
                  ? "bg-highlight-lighter border"
                  : ""
                } ${isResponsiveView ? "mobile-week-header" : ""}`}
              key={index}
            >
              {isResponsiveView ? day.substring(0, 3) : day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 24 }).map((_, hour) => (
          <tr key={hour}>
            <td className="display-flex justify-content-center align-items-center">{`${hour < 10 ? `0${hour}` : hour}:00`}</td>
            {Array.from({ length: 7 }).map((_, day) => {
              return (
                <td
                  style={{
                    height: isResponsiveView ? 60 : 96,
                    width: isResponsiveView ? 80 : 100,
                    minWidth: isResponsiveView ? 80 : 100
                  }}
                  className={`border ${
                    currentDate.getDate() === firstWeekDay.getDate() + day
                      ? "bg-highlight-decent"
                      : ""
                  }`}
                  key={day}
                >
                  {getCard(addDays(firstWeekDay, day), hour)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
};

export default Week;
